import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/mui";

import { formatDateTime, kindColor } from "./kinds";

type CreationDetail = {
  id: number;
  kind: string;
  itemId: string | null;
  storyId: number;
  userId: number;
  title: string;
  status: string;
  data: unknown;
  settings: unknown;
  createdAt: string;
  updatedAt: string;
};

/**
 * One `creations` row, read whole (`GET /api/v2/admin/creations/:id`).
 *
 * A Story Studio item (`kind` ≠ "story": a world, voice, style, draft or a bin
 * entry) is its `data` JSON:
 * the engine's own record, shown as it holds it. Its other columns are a
 * projection the engine rewrites, and the server refuses to edit or delete
 * it (409), so the page is read-only. A v1 story shows its `settings` JSON.
 */
export const CreationShow = () => {
  const { query } = useShow<CreationDetail>({ resource: "admin/creations" });
  const { data, isLoading } = query;
  const record = data?.data;
  const studio = record ? record.kind !== "story" : false;
  const json = studio ? record?.data : record?.settings;

  return (
    <Show
      isLoading={isLoading}
      resource="admin/creations"
      canEdit={false}
      canDelete={!studio}
      title={<Typography variant="h5">{record?.title || record?.itemId || `Creation #${record?.id ?? ""}`}</Typography>}
    >
      {record ? (
        <Stack gap={2}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" label={record.kind} color={kindColor(record.kind)} />
            {studio ? null : <Chip size="small" variant="outlined" label={record.status} />}
            <Typography variant="caption" color="text.secondary">
              id {record.id}
              {record.itemId ? ` · item ${record.itemId}` : ""}
              {studio ? "" : ` · storyId ${record.storyId}`} · userId {record.userId} · created{" "}
              {formatDateTime(record.createdAt)} · updated {formatDateTime(record.updatedAt)}
            </Typography>
          </Stack>

          <Box>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              {studio ? "Data (JSON)" : "Settings (JSON)"}
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                borderRadius: 1,
                bgcolor: "action.hover",
                fontFamily: "monospace",
                fontSize: 13,
                lineHeight: 1.5,
                overflow: "auto",
                maxHeight: "70vh",
                whiteSpace: "pre",
              }}
            >
              {json == null ? "null" : JSON.stringify(json, null, 2)}
            </Box>
          </Box>
        </Stack>
      ) : null}
    </Show>
  );
};
